import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaCheckCircle, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import { apiURL, filesURL, sitePath } from 'config/globals'
import Topbar from 'components/Topbar'
import CraftRecordNavigation from 'components/CraftRecordNavigation'
import FormRow from 'components/FormRow'
import { checkAuth } from 'utils/Auth'
import ReactImagePickerEditor from 'react-image-picker-editor'
import 'react-image-picker-editor/dist/index.css'
import { Button, Modal } from "react-bootstrap"
import StickyFooter from "components/StickyFooter"

const productPurposeOptions = [
    { value: '', label: '' },
    { value: 'use', label: 'Use' },
    { value: 'decorative', label: 'Decorative' },
]

const distributionOptions = [
    { value: '', label: '' },
    { value: 'shops', label: 'Shops' },
    { value: 'markets', label: 'Markets / Fairs' },
    { value: 'straight', label: 'Straight from the workshop' },
    { value: 'other', label: 'Other ways' },
]

const CraftRecord7 = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [craftRecord, setCraftRecord] = useState({
        type: 'traditional'
    })
    const [form, setForm] = useState({})
    const [loadError, setLoadError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState('')
    const [successMessage, setSuccessMessage] = useState('')

    const navigate = useNavigate()
    const { entityId, craftRecordId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            if (craftRecordId) {
                await getCraftRecord(craftRecordId)
            }
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const getCraftRecord = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-craft-record/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load record data: " + (response.data.error || response.data))
                return
            }

            const record = response.data.item
            setCraftRecord(record)
            setForm({
                productPurposeType: record.productPurposeType,
                distributionType: record.distributionType,
            })
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const save = async (dataUri) => {
        setSubmitError('')
        setIsSubmitting(true)

        const submittedForm = {...form}
        if (dataUri) {
            submittedForm.productsMadeImage = dataUri
        }
        
        try {
            const response = await axios.put(`${apiURL}/update-craft-record/${craftRecord.id}`, submittedForm)
            
            if (!response.data.success) {
                setSubmitError(response.data.error || response.data)
                setIsSubmitting(false)
                return
            }

            setIsSubmitting(false)
        } catch (error) {
            setSubmitError(JSON.stringify(error))
            setIsSubmitting(false)
        }
    }

    const previousPage = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records/${craftRecord.id}/edit-process`)
    }

    const finish = () => {
        navigate(`/museum/${museum.id}/documentation/craft-records`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 300, height: 'fit-content'}}>
                {museum.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/craft-records`} className="no-decoration"><FaAngleLeft /> Back to Craft Records</Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>Craft Record</h1>
                            </div>
                        </header>
                        <CraftRecordNavigation currentTab="product" museumId={museum.id} craftRecordId={craftRecord.id} />
                        {(!craftRecordId || craftRecord.id) &&
                            <>
                                <h3>Products / artefacts made</h3>
                                <FormRow
                                    key="productsMadeDesc"
                                    label="Description"
                                    content={
                                        <Form.Control
                                            name="productsMadeDesc"
                                            placeholder="Describe the products or artifacts made"
                                            defaultValue={craftRecord.productsMadeDesc}
                                            as="textarea"
                                            rows="5"
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        />
                                    }
                                />
                                <FormRow
                                    key="productsMadeImage"
                                    label="Image of product"
                                    hint={<>Accepted types: JPG or PNG<br/>Recommended size: 1200x800</>}
                                    content={
                                        <div className="mb-3">
                                            <ReactImagePickerEditor
                                                config={{
                                                    borderRadius: '8px',
                                                    language: 'en',
                                                    width: '330px',
                                                    height: '250px',
                                                    objectFit: 'contain',
                                                    compressInitial: null,
                                                }}
                                                imageSrcProp={craftRecord.productsMadeImage
                                                    ? `${filesURL}/${craftRecord.productsMadeImage}`
                                                    : null
                                                }
                                                imageChanged={dataUri => {
                                                    handleChange('productsMadeImage', {
                                                        uri: dataUri
                                                    })
                                                    save({
                                                        uri: dataUri
                                                    })
                                                }}
                                            />
                                        </div>
                                    }
                                />
                                <h3>Purpose of the products</h3>
                                <FormRow
                                    key="productPurposeType"
                                    label="Type of purpose"
                                    content={
                                        <Form.Select
                                            name="productPurposeType"
                                            defaultValue={craftRecord.productPurposeType}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        >
                                            {productPurposeOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))
                                            }
                                        </Form.Select>
                                    }
                                />
                                {!!form.productPurposeType &&
                                    <FormRow
                                        key="productPurposeDesc"
                                        label="Description"
                                        content={
                                            <Form.Control
                                                name="productPurposeDesc"
                                                placeholder="Describe the purpose of the product"
                                                defaultValue={craftRecord.productPurposeDesc}
                                                as="textarea"
                                                rows="5"
                                                onChange={e => handleChange(e.target.name, e.target.value)}
                                                onBlur={save}
                                            />
                                        }
                                    />
                                }
                                <h3 className="mt-4">Distribution</h3>
                                <FormRow
                                    key="distributionType"
                                    label="Type of distribution"
                                    content={
                                        <Form.Select
                                            name="distributionType"
                                            defaultValue={craftRecord.distributionType}
                                            onChange={e => handleChange(e.target.name, e.target.value)}
                                            onBlur={save}
                                        >
                                            {distributionOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))
                                            }
                                        </Form.Select>
                                    }
                                />
                                {form.distributionType === 'shops' &&
                                    <FormRow
                                        key="distributionShops"
                                        label="Where?"
                                        content={
                                            <Form.Control
                                                name="distributionShops"
                                                defaultValue={craftRecord.distributionShops}
                                                onChange={e => handleChange(e.target.name, e.target.value)}
                                                onBlur={save}
                                            />
                                        }
                                    />
                                }
                                {form.distributionType === 'markets' &&
                                    <FormRow
                                        key="distributionMarkets"
                                        label="Which ones?"
                                        content={
                                            <Form.Control
                                                name="distributionMarkets"
                                                defaultValue={craftRecord.distributionMarkets}
                                                onChange={e => handleChange(e.target.name, e.target.value)}
                                                onBlur={save}
                                            />
                                        }
                                    />
                                }
                                {form.distributionType === 'other' &&
                                    <FormRow
                                        key="distributionOther"
                                        label="Which?"
                                        content={
                                            <Form.Control
                                                name="distributionOther"
                                                defaultValue={craftRecord.distributionOther}
                                                onChange={e => handleChange(e.target.name, e.target.value)}
                                                onBlur={save}
                                            />
                                        }
                                    />
                                }
                                {submitError && 
                                    <Alert key='submitError' variant='danger'>
                                        {submitError}
                                    </Alert>
                                }
                            </>
                        }
                    </>
                }
                {loadError &&
                    <>
                        <Alert key='loadError' variant='danger'>
                            {loadError}
                        </Alert>
                    </>
                }
                {successMessage &&
                    <Modal show={!!successMessage} onHide={() => setSuccessMessage("")} backdrop="static" centered>
                        <Modal.Header>
                            <Modal.Title className="text-success">
                                <FaCheckCircle /> Success
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {successMessage}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" onClick={finish}>
                                View craft records
                            </Button>
                        </Modal.Footer>
                    </Modal>
                }
                <StickyFooter>
                    <Button size="lg" variant="outline-primary" className="float-start" onClick={previousPage}>
                        <FaAngleLeft /> Previous
                    </Button>
                    <Button size="lg" variant="primary" className="float-end" onClick={finish}>
                        Finish
                    </Button>
                </StickyFooter>
            </main>
        </>
    )
}

export default CraftRecord7